function asPlainObject(value) {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return {};
    }
    return {};
  }
  if (typeof value === "object" && !Array.isArray(value)) return value;
  return {};
}

/** Merge notification API `data` and `deepLink` maps (same rules as mobile AppNotification handling). */
export function mergeNotificationPayload(notification) {
  const raw = notification && typeof notification === "object" ? notification : {};
  const data = asPlainObject(raw.data);
  const nestedData = asPlainObject(data.data);
  const deepLink = asPlainObject(raw.deepLink);
  const merged = { ...nestedData, ...data, ...deepLink };

  const rootKeys = [
    "workoutId",
    "workout_id",
    "exerciseIndex",
    "exercise_index",
    "conversationId",
    "conversation_id",
    "screen",
    "action",
  ];
  for (const key of rootKeys) {
    if (raw[key] != null && merged[key] == null) merged[key] = raw[key];
  }
  if (raw.conversationId != null) merged.conversationId = raw.conversationId;
  if (raw.conversation_id != null)
    merged.conversationId = raw.conversation_id;
  return merged;
}

export function normalizeNotificationsResponse(body) {
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  if (typeof body === "object") {
    if (Array.isArray(body.items)) return body.items;
    if (Array.isArray(body.notifications)) return body.notifications;
  }
  return [];
}

export function isUnreadNotification(notification) {
  if (!notification || typeof notification !== "object") return false;
  const status = (notification?.status ?? "").toString().toLowerCase();
  const read =
    notification?.isRead === true ||
    notification?.read === true ||
    notification?.seen === true ||
    notification?.readAt != null ||
    notification?.seenAt != null ||
    status === "read";
  return !read;
}

export function isMessageNotification(notification) {
  if (!notification || typeof notification !== "object") return false;

  const payload = mergeNotificationPayload(notification);
  const screen = payload?.screen?.toString().toLowerCase() ?? "";
  if (screen === "messages") return true;

  const type = (notification.type ?? notification.notificationType ?? "")
    .toString()
    .toLowerCase();
  const title = (notification.title ?? notification.subject ?? "")
    .toString()
    .toLowerCase();
  const body = (
    notification.body ??
    notification.message ??
    notification.content ??
    notification.text ??
    ""
  )
    .toString()
    .toLowerCase();

  const conversationId =
    payload?.conversationId ?? payload?.conversation_id ?? null;

  if (conversationId != null) return true;
  if (
    type.includes("message") ||
    type.includes("conversation") ||
    type.includes("chat")
  ) {
    return true;
  }

  const haystack = `${title} ${body}`;
  return (
    haystack.includes("message") ||
    haystack.includes("chat") ||
    haystack.includes("conversation")
  );
}

export function isCommentNotification(notification) {
  if (!notification || typeof notification !== "object") return false;
  if (isMessageNotification(notification)) return false;

  const type = (notification.type ?? notification.notificationType ?? "")
    .toString()
    .toLowerCase();
  if (type === "comment" || type.includes("comment")) return true;

  const payload = mergeNotificationPayload(notification);
  const screen = payload?.screen?.toString().toLowerCase() ?? "";
  const action = payload?.action?.toString().toLowerCase() ?? "";
  const workoutId = payload?.workoutId ?? payload?.workout_id;
  // Workout reminders also use LogWorkout + workoutId; only comment deep-links
  // include openComments / an exercise index.
  if (
    screen === "logworkout" &&
    workoutId != null &&
    (action === "opencomments" ||
      payload?.exerciseIndex != null ||
      payload?.exercise_index != null)
  ) {
    return true;
  }

  const title = (notification.title ?? notification.subject ?? "")
    .toString()
    .toLowerCase();
  const body = (
    notification.body ??
    notification.message ??
    notification.content ??
    notification.text ??
    ""
  )
    .toString()
    .toLowerCase();
  const haystack = `${title} ${body}`;
  return haystack.includes("comment") || haystack.includes("reply");
}

export function notificationMatchesConversation(notification, conversationId) {
  if (conversationId == null || conversationId === "") return false;
  const payload = mergeNotificationPayload(notification);
  const id = payload?.conversationId ?? payload?.conversation_id;
  return id != null && String(id) === String(conversationId);
}

export function notificationMatchesWorkoutComment(
  notification,
  workoutId,
  exerciseIndex
) {
  if (workoutId == null || workoutId === "") return false;
  if (!isCommentNotification(notification)) return false;

  const payload = mergeNotificationPayload(notification);
  const notificationWorkoutId = payload.workoutId ?? payload.workout_id;
  if (String(notificationWorkoutId ?? "").trim() !== String(workoutId).trim()) {
    return false;
  }
  if (exerciseIndex == null || exerciseIndex === undefined) return true;

  const notificationExerciseIndex =
    payload.exerciseIndex ?? payload.exercise_index;
  // Older comment notifications may omit exerciseIndex; still the same thread.
  if (
    notificationExerciseIndex == null ||
    notificationExerciseIndex === ""
  ) {
    return true;
  }
  return String(notificationExerciseIndex) === String(exerciseIndex);
}

export const NOTIFICATIONS_CHANGED_EVENT = "trainer-notifications-changed";

export function dispatchNotificationsChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED_EVENT));
  }
}
